import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { jobDetail } from 'actions/jobDetails';
import { jobDetailsPayloadType } from 'type/JobsType';

export const useJobDetails = () => {
  const params = useParams();
  const { id = '' } = params;
  const dispatch = useDispatch();
  const [paginationValues, setPaginationValues] = useState({
    pageNumber: 1,
    rpp: 10
  });
  const { pageNumber, rpp } = paginationValues;
  // State
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [sorting, setSorting] = useState<{
    column: string;
    direction: 'asc' | 'desc' | '';
  }>({
    column: 'created_date',
    direction: 'asc'
  });
  const [alert,setAlert] = useState({ open: false, message: '' });
  const [refresh, setRefresh] = useState(false);
  const [filteredResults, setFilteredResults] = useState({
    records: [] as any[],
    totalRecords: 0
  });
  const [searchInput, setSearchInput] = useState('');

  // Selectors
  const { userName } = useSelector((state: any) => state.userTypeReducer?.userType?.data) || {};
  const jobsDetailsData = useSelector((state: any) => state?.jobDetailsReducer?.jobDetails);
  const isLoading = useSelector((state: any) => state.jobDetailsReducer.loading);
  const error = useSelector((state: any) => state.jobDetailsReducer.error);

  // Update filtered results when jobsData changes
  useEffect(() => {
    if (jobsDetailsData?.list?.length > 0) {
      setFilteredResults({
        records: jobsDetailsData.list,
        totalRecords: jobsDetailsData.totalCount
      });
    } else {
      setFilteredResults({ records: [], totalRecords: 0 });
    }
  }, [jobsDetailsData]);

  // Reset filters when job ID changes (navigating to different job)
  useEffect(() => {
    dispatch(jobDetail.clearJobDetails() as any);
    setSelectedStatus([]);
    setFilteredResults({ records: [], totalRecords: 0 });
    setSearchInput('');
    setPaginationValues({ pageNumber: 1, rpp: 10 });
    setSorting({ column: 'created_date', direction: 'asc' });
  }, [id]);

  const fetchJobDetails = useCallback(async (statusFilter?: string[]) => {
    // Use the provided status filter or current selectedStatus
    const statusToUse = statusFilter !== undefined ? statusFilter : selectedStatus;
    
    const payload: jobDetailsPayloadType = {
      bulkUploadId: id,
      pageNumber: pageNumber,
      sortType: sorting.direction.toUpperCase(),
      sortColumn: sorting.column,
      rpp: rpp,
      status: statusToUse.length > 0 ? statusToUse : [], // Join multiple statuses with comma
      searchKeyword: searchInput
    };
    dispatch(jobDetail.fetchJobDetails(payload) as any);
  }, [id, pageNumber, sorting, rpp, searchInput, selectedStatus, dispatch]);

  // Fetch jobs when dependencies change
  useEffect(() => {
    fetchJobDetails();
  }, [pageNumber, rpp, sorting, selectedStatus, dispatch, userName, refresh]);

  const searchHandler = () => {
    if( searchInput.length < 3 ) {
      setAlert({
        open: true,
        message: 'Please enter at least 3 characters to search.'
      });
      return;
    }
    fetchJobDetails();
  };

  // Handlers
  const handleRefreshClick = () => {
    setSorting({ column: 'created_date', direction: 'asc' });
    setRefresh(!refresh);
  };

  const handleSortingChange = (column: string, direction: 'asc' | 'desc' | '') => {
    setPaginationValues({ ...paginationValues, pageNumber: 1 });
    setSorting({ column, direction });
  };


  const handleActivePageChange = (page: any) => {
    window.scrollTo(0, 0);
    setPaginationValues({ ...paginationValues, pageNumber: page });
  };

  const searchItems = (searchValue: string) => {
    window.scrollTo(0, 0);
    setSearchInput(searchValue);
    if(searchValue.trim().length === 0){
      handleClearSearch();
    }
  };

  const handleResultsPerPageChange = (size: any) => {
    window.scrollTo(0, 0);
    setPaginationValues({ ...paginationValues, pageNumber: 1, rpp: size });
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setPaginationValues({ ...paginationValues, pageNumber: 1, rpp: 10 });
    // Reset the search by creating a new payload with empty searchKeyword
    const payload: jobDetailsPayloadType = {
      bulkUploadId: id,
      pageNumber: 1,
      sortType: sorting.direction.toUpperCase(),
      sortColumn: sorting.column,
      rpp: 10,
      status: selectedStatus.length > 0 ? selectedStatus : [],
      searchKeyword: '' // Explicitly set empty string
    };
    dispatch(jobDetail.fetchJobDetails(payload) as any);
  };

  // Generate unique statuses with counts and colors
  const generateStatusStats = useCallback(() => {
    
    if (!jobsDetailsData?.jobsConsolidatedStatus) {
      console.log('No jobsConsolidatedStatus found, returning empty array');
      return [];
    }
    const statusCounts = jobsDetailsData.jobsConsolidatedStatus;
    const result = Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count: count as number,
      label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replaceAll('_', ' '),
    //   color: getStatusColor(status.toLowerCase())
    }));
    return result;
  }, [jobsDetailsData]);

  const statusStats = generateStatusStats();

  // Generate page size options
  const generatePageSizeOptions = (totalRecords: number) => {
    const options = [5, 10];
    if (totalRecords > 10) {
      options.push(20);
    }
    if (totalRecords > 20) {
      options.push(30);
    }
    if (totalRecords > 30) {
      options.push(50);
    }
    const filteredOptions = options.filter(option => option <= totalRecords || option === 10);
    return filteredOptions.length > 0 ? filteredOptions : [10];
  };

  const rppList = generatePageSizeOptions(filteredResults.totalRecords);

  // Handle status filter clicks
  const handleStatusClick = (status: string) => {
    const newSelectedStatus = selectedStatus.includes(status)
      ? selectedStatus.filter(s => s !== status)
      : [...selectedStatus, status];
    
    // Reset pagination when status filter changes
    setPaginationValues({ ...paginationValues, pageNumber: 1 });
    setSelectedStatus(newSelectedStatus);
  };

  const handleClearAllFilters = () => {
    // Reset pagination when clearing filters
    setPaginationValues({ ...paginationValues, pageNumber: 1 });
    setSelectedStatus([]);
  };

  return {
    pageNumber,
    rpp,
    sorting,
    searchInput,
    filteredResults,
    isLoading,
    error,
    rppList,
    alert,
    setAlert,
    selectedStatus,
    setSelectedStatus,
    statusStats,
    handleStatusClick,
    handleClearAllFilters,
    
    // Handlers
    searchHandler,
    handleRefreshClick,
    handleSortingChange,
    handleActivePageChange,
    searchItems,
    handleResultsPerPageChange,
    handleClearSearch,
    fetchJobDetails
  };
};
